import React, { useState, useMemo, useCallback } from 'react';
import { NavLink } from 'react-router-dom';
import { FaBars, FaTimes } from 'react-icons/fa';
import { Home, Scale, Newspaper, Book, Gavel, Scissors, Headphones, Video, FileText, Layers } from 'lucide-react';

import TopMenuMobile from './TopMobile';
import NavigationMenu from './NavigationMenu';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';
import ThemeSwitcher from '../components/ThemeSwitcher';
import { textColor, borderColor } from '../styles/styles';
import clsx from 'clsx';
import { useTheme } from '../context/ThemeProvider';
import lhrc_logo from '../assets/logo/lhrc_logo.svg';
import lhrc_logo_light from '../assets/logo/lhrc_logo_light.svg';

const SearchForm = React.memo(({ tr }) => (
    <form className={clsx('hidden lg:flex items-center max-w-sm mx-auto')}>
        <div className="relative w-full">
            <div className="absolute inset-y-0 start-0 flex items-center pl-3 pointer-events-none">
                <svg className="w-4 h-4 text-gray-500 dark:text-gray-400" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20">
                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z" />
                </svg>
            </div>
            <input
                type="text"
                className={clsx(`
                bg-gray-50 dark:bg-gray-700/50 
                pl-10
                border border-gray-300 dark:border-gray-600
                text-gray-900 dark:text-gray-100
                text-sm rounded-lg p-2.5 w-full
                focus:outline-none focus:ring-0 focus:border-[#41A6D7] 
                 `,
                    textColor
                )}
                placeholder={tr('search')}
                aria-label={tr('search')}
            />
        </div>
    </form>
));

const AuthButtons = React.memo(({ tr }) => (
    <div className="hidden lg:flex space-x-2 justify-center">
        <NavLink
            to="/login"
            className="text-white bg-[#41A6D7] hover:bg-[#3590BA] 
                       dark:bg-[#256B8A] dark:hover:bg-[#1E5A77]
                       focus:ring-4 focus:ring-blue-300 dark:focus:ring-blue-800 
                       font-medium rounded-lg text-sm px-5 py-2.5 focus:outline-none"
        >
            {tr('login')}
        </NavLink>
        {/* <NavLink
            to="/register"
            className="text-white bg-[#41A6D7] hover:bg-[#3590BA] 
                       dark:bg-[#256B8A] dark:hover:bg-[#1E5A77]
                       focus:ring-4 focus:ring-blue-300 dark:focus:ring-blue-800 
                       font-medium rounded-lg text-sm px-5 py-2.5 focus:outline-none"
        >
            {tr('register')}
        </NavLink> */}
        <LanguageSwitcher />
        <ThemeSwitcher />
    </div>
));


const Header = () => {
    const { t } = useTranslation();
    const tr = useCallback((key) => t(`header:${key}`), [t]);

    const { theme } = useTheme();
    const logo = theme === 'dark' ? lhrc_logo_light : lhrc_logo;

    const menuItems = useMemo(
        () => [
            { to: '/', label: tr('home'), icon: Home },
            { to: '/constitution', label: tr('constitution'), icon: Scale },
            { to: '/journals', label: tr('journals'), icon: Newspaper },
            { to: '/books', label: tr('books'), icon: Book },
            { to: '/reports', label: tr('reports'), icon: FileText },
            { to: '/cases', label: tr('cases'), icon: Gavel },
            { to: '/cuttings', label: tr('cuttings'), icon: Scissors },
            { to: '/audios', label: tr('audios'), icon: Headphones },
            { to: '/videos', label: tr('videos'), icon: Video },
            { to: '/other', label: tr('other'), icon: Layers },
        ],
        [tr]
    );

    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const toggleMobileMenu = useCallback(() => {
        setIsMobileMenuOpen((prev) => !prev);
    }, []);

    return (
        <header className="sticky w-full top-0 z-50 mb-6">
            <nav className={clsx('bg-white dark:bg-gray-800', borderColor, 'border-b')}>
                <div
                    className={clsx(
                        'flex flex-wrap justify-between',
                        'max-w-screen-3xl px-4 sm:px-6 lg:px-24 xl:px-32 2xl:px-64 mx-auto',
                        'items-center p-4',
                        'lg:p-6'
                    )}
                >
                    <a href="/" className="flex items-center space-x-3 rtl:space-x-reverse">
                        <img src={logo} alt="LHRC Logo" className="w-[70px] sm:w-[85px] md:w-[90px] lg:w-[100px] h-auto mb-0" />
                        <div className={clsx('text-[#50A1CA] dark:text-white font-bold text-4xl  mb-1')}>Katiba</div>
                    </a>
                    <div className="lg:hidden">
                        <button onClick={toggleMobileMenu} className="text-gray-600" aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'} aria-expanded={isMobileMenuOpen}>
                            {isMobileMenuOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
                        </button>
                    </div>
                    <div className="flex items-center space-x-2">
                        <SearchForm tr={tr} />
                        <AuthButtons tr={tr} />
                    </div>
                </div>
            </nav>
            <nav className={clsx('hidden lg:flex border-t border-b mb-2', 'bg-white dark:bg-gray-800', borderColor)}>
                <div className="max-w-screen-xl px-4 py-5 gap-4 mx-auto lg:block">
                    <NavigationMenu items={menuItems} />
                </div>
            </nav>
            {isMobileMenuOpen && (
                <nav className="lg:hidden bg-transparent shadow-lg shadow-gray-300/40 fixed top-14 left-0 w-full z-50">
                    <div className="max-w-screen-xl py-3">
                        <TopMenuMobile items={menuItems} toggleMobileMenu={toggleMobileMenu} />
                    </div>
                </nav>
            )}
        </header>
    );
};

export default React.memo(Header);
